import React, { useCallback, useContext, useState } from 'react';
import { style } from 'typestyle';
import Modal from 'react-modal';
import DeleteIcon from 'mdi-react/DeleteOutlineIcon';
import { useTranslation } from 'react-i18next';
import * as styles from './modal.styles';
import ModalActionCard from '../cards/modal-action-card.component';
import ResendEmailModal from './resend-email-modal.component';
import type OfficeManagerUser from '../models/officeManagerUser';
import PrimaryButton from '../../../core/components/buttons/primary-button.component';
import Column from '../../../core/components/column/column.component';
import { CrossAxisAlignment, MainAxisAlignment } from '../../../core/components/flex/flex.component';
import notify from '../../../core/components/notify/notify.component';
import Row from '../../../core/components/row/row.component';
import { IsMobileContext } from '../../../core/context/is-mobile';
import ConfirmationModal from '../../offices/modals/confirmation-modal.component';
import { resendCredentialsByEmail } from '../user/user.service';
import { useKeycloak } from '../keycloak/KeycloakProvider';

interface Props {
  readonly showModal: boolean;
  readonly user: OfficeManagerUser;
  readonly handleClose: () => void;
  readonly handleDeleteOfficeManager: (user: OfficeManagerUser) => void;
}

const OfficeManagerModal = ({ showModal, user, handleClose, handleDeleteOfficeManager }: Props): React.JSX.Element => {
  const isMobile = useContext(IsMobileContext);
  const { t } = useTranslation();
  const keycloak = useKeycloak();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showResendEmailModal, setShowResendEmailModal] = useState(false);

  const deleteUser = (): void => {
    setShowDeleteModal(false);
    handleDeleteOfficeManager(user);
    handleClose();
  };

  const resendEmail = useCallback(async (): Promise<void> => {
    setShowResendEmailModal(false);
    if (user?.email != null) {
      const success = await resendCredentialsByEmail(user.email, keycloak);
      if (success) {
        notify(t('email-sending-success-message'), true);
        return;
      }
    }

    notify(t('email-sending-error-message'), false);
  }, [user, keycloak, t]);

  return (
    <>
      <Modal ariaHideApp={false} isOpen={showModal} onRequestClose={handleClose} style={styles.modalStyle(isMobile)}>
        <Column crossAxisAlignment={CrossAxisAlignment.Stretch} itemSpacing={12}>
          <span className={style(styles.columnHeader)}>{t('office-manager')}</span>
          <ModalActionCard
            text={t('resend-login-details')}
            onClick={(): void => {
              setShowResendEmailModal(true);
            }}
          />
          <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
            <span className={style(styles.modalTitle)}>{t('email')}</span>
            <span className={style(styles.modalBody)}>{user.email}</span>
          </Row>
          <Row mainAxisAlignment={MainAxisAlignment.Stretch}>
            <span className={style(styles.modalTitle)}>{t('office-name')}</span>
            <span className={style(styles.modalBody)}>{user.officeName}</span>
          </Row>
          <Row mainAxisAlignment={MainAxisAlignment.Stretch} crossAxisAlignment={CrossAxisAlignment.Center}>
            <span className={style(styles.modalTitle)}>{t('role')}</span>
            <span className={style(styles.modalBody)}>{t('office-manager')}</span>
          </Row>
          {keycloak.tokenParsed?.email !== user.email && (
            <Row
              className={style(styles.deleteButton)}
              mainAxisAlignment={MainAxisAlignment.Stretch}
              crossAxisAlignment={CrossAxisAlignment.Center}
              onClick={(): void => {
                setShowDeleteModal(true);
              }}
            >
              <span>{t('delete-user')}</span>
              <DeleteIcon className={style(styles.deleteIcon)} />
            </Row>
          )}

          <div className={style(styles.button)}>
            <PrimaryButton fullWidth onClick={handleClose}>
              {t('close')}
            </PrimaryButton>
          </div>
        </Column>
      </Modal>
      <ConfirmationModal
        showModal={showDeleteModal}
        itemName={user.email}
        handleAccept={deleteUser}
        handleClose={(): void => {
          setShowDeleteModal(false);
        }}
      />
      <ResendEmailModal
        showModal={showResendEmailModal}
        handleAccept={(): void => {
          resendEmail();
        }}
        handleClose={(): void => {
          setShowResendEmailModal(false);
        }}
      />
    </>
  );
};

export default OfficeManagerModal;
